import { round } from "@repo/system";
import { type Allowance } from "~/api/products/types";
import { type PosProductInBasket } from "~/providers/store/basket/types";
import { type StockStatus } from "~/utils/stock/infer-stock-status";
import { type UserLimitClient } from "~/utils/user-limits/types";

/**
 * get the product price including hybrid billing
 */
const getInitialTotalPrice = (product: PosProductInBasket, amount?: number) => {
  let newTotalPrice = product.totalUnitPrice * (amount || product.quantity);

  // if hybrid billing
  if (product.discount?.amount) {
    newTotalPrice -= product.discount.amount * (amount || product.quantity);
  }

  return newTotalPrice / 100;
};

/**
 * calculate the new price after discount
 */
const calculateDiscountedPrice = ({
  product,
  amount,
  percentage,
  discount
}: {
  product: PosProductInBasket;
  amount: number;
  percentage?: number;
  discount?: number;
}) => {
  let newPrice = getInitialTotalPrice(product, amount);

  if (typeof percentage === "number") {
    newPrice -= newPrice * (percentage / 100);
  } else {
    newPrice -= discount || 0;
  }
  return round(newPrice, 2);
};

/**
 * get the maximum amount of products that can be added to the basket
 */
const getMaxAmount = (
  value: UserLimitClient | Allowance | undefined,
  { quantity, discount }: Pick<PosProductInBasket, "quantity" | "discount">
) => {
  if (value && "type" in value) {
    if (!discount) return null;

    if (value.type === "numberOfProducts") {
      return quantity + value.remaining;
    }

    // `discount.amount` is 0 on a spent allowance, and the line below divides.
    if (!discount.amount) {
      return value.isAllowanceOver ? quantity : null;
    }

    return round(value.remaining / discount.amount, 0) + quantity;
  }

  if (!value) return null;

  return quantity + value.remaining;
};

type GetLimitArgs = {
  amount: number;
  userLimitMaxAmount: number | null;
  allowanceMaxAmount: number | null;
  stockStatus: StockStatus;
};

/**
 * get the limit of products that can be added to the basket
 */
function getLimit({
  amount,
  userLimitMaxAmount,
  allowanceMaxAmount,
  stockStatus
}: GetLimitArgs) {
  if (stockStatus === "reserved" || stockStatus === "out-of-stock") {
    return amount;
  }

  return Math.min(
    userLimitMaxAmount ?? Infinity,
    allowanceMaxAmount ?? Infinity,
    typeof stockStatus === "number" ? stockStatus : Infinity
  );
}

export {
  calculateDiscountedPrice,
  getInitialTotalPrice,
  getLimit,
  getMaxAmount
};
